/**
 * Multi-language related operations
 */
import { Locale } from 'ant-design-vue/es/locale';
import { computed, unref } from 'vue';
import { LOCALE, type LocaleType, setHtmlPageLang } from '@mts-fe/utils';
import { changeDevExtremeLocale } from '@mts/locales/dx';
import { registeGlobalParam } from '@mts/utils';
import { PCMode } from '@mts/utils/env';
import { getLocaleStore } from '@/store';
import { loadLocaleMsg } from './helper';
import { i18n } from './setupI18n';

interface LangModule {
  message: Recordable;
  dateLocale: Recordable;
  dateLocaleName: string;
}

async function setI18nLanguage(locale: LocaleType) {
  await loadLocaleMsg(locale);

  if (i18n.mode === 'legacy') {
    i18n.global.locale = locale;
  } else {
    (i18n.global.locale as any).value = locale;
  }
  getLocaleStore().setLocaleInfo({ locale });
  setHtmlPageLang(locale);

  PCMode && (await changeDevExtremeLocale(locale));
}

export function useLocale() {
  const localeStore = getLocaleStore();
  const getLocale = computed(() => localeStore.getLocale);
  const getShowLocalePicker = computed(() => localeStore.getShowPicker);

  const getAntdLocale = computed((): any => {
    const localeMessage = i18n.global.getLocaleMessage<{ antdLocale: Locale }>(unref(getLocale));
    return localeMessage?.antdLocale ?? {};
  });

  // Switching the language will change the locale of useI18n
  // And submit to configuration modification
  async function changeLocale(locale: LocaleType) {
    const globalI18n = i18n.global;
    const currentLocale = unref(globalI18n.locale);
    if (currentLocale === locale) {
      return locale;
    }

    const langModule = ((await import(`./lang/${locale}.ts`)) as any).default as LangModule;
    if (!langModule) return;

    globalI18n.setLocaleMessage(locale, langModule.message);

    await setI18nLanguage(locale);
    return locale;
  }

  registeGlobalParam({
    i18n,
    changeLocale,
    getLocaleMsg: (locale = LOCALE.zh_CN) => i18n.global.getLocaleMessage(locale),
  });

  return {
    getLocale,
    getShowLocalePicker,
    changeLocale,
    getAntdLocale,
  };
}
